import { useAnalytics } from '@medely/ui-kit';
import { JobDetailsFragment } from '@mp/components/jobs/jobDetailsComponents/graphql';
import {
  FormValuesProps,
  UtmParamsType,
} from '@mp/components/jobs/jobDetailsComponents/jobCancelationPolicy/interface';
import { QUERY_KEYS } from '@mp/constants/queryKeys';
import { useProNavigate } from '@mp/contexts';
import { usePerDiemEventTracking, useRollbar } from '@mp/hooks';
import { useNetworkRequest } from '@mp/hooks/useNetworkRequest';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FragmentType, parseFragmentData } from './generated';

type UseCancelShiftButtonProps = {
  utmParams?: UtmParamsType;
  job?: FragmentType<typeof JobDetailsFragment> | null;
  isAssignment: boolean;
  onSuccess?: () => void;
};

export const useCancelShiftButton = ({
  utmParams,
  isAssignment,
  job: inputJob,
  onSuccess,
}: UseCancelShiftButtonProps) => {
  const job = parseFragmentData(JobDetailsFragment, inputJob);
  const analytics = useAnalytics();
  const { trackCanceled } = usePerDiemEventTracking(analytics);
  const { navigate } = useProNavigate();
  const { axios } = useNetworkRequest();
  const { rollbar } = useRollbar();

  const client = useQueryClient();

  const createRequest = (values: FormValuesProps) => {
    const trackingData = {
      tracking_params: {
        utm_source: utmParams?.utm_source,
        utm_campaign: utmParams?.utm_campaign,
      },
    };
    const cancellationReasonData = {
      cancellation_reason_id: values.cancellation_reason_id,
      other_reason: values.other_reason,
    };
    const jobData = {
      cancel: {
        ...cancellationReasonData,
        ...trackingData,
      },
    };
    const assignmentData = {
      cancel: {
        ...cancellationReasonData,
      },
    };

    return isAssignment ? assignmentData : jobData;
  };

  const { mutate: cancel, isLoading } = useMutation(
    (values: FormValuesProps) => {
      return axios.post(`/v3/professional/jobs/${job?.id}/cancel`, createRequest(values));
    },
    {
      onSuccess: ({ data }) => {
        const jobQueryKeys = QUERY_KEYS.JOB;

        trackCanceled(data.payload.id);
        client.removeQueries([jobQueryKeys.list]);
        client.removeQueries([jobQueryKeys.detail, job?.id]);
        client.removeQueries([jobQueryKeys.available]);
        onSuccess?.();
        navigate('Shifts', {
          backRoute: 'Today',
        });
      },
      onError: (error, data) => {
        rollbar.error('[MP] Something went wrong while canceling the job', {
          jobId: job?.id,
          isAssignment,
          data,
          job: job,
          error,
        });
      },
    },
  );
  return { cancel, isLoading };
};
